import React from 'react';

import { Nullable } from '@clearverify/types';

import {
  GeneratedReportsMetaDocument,
  GeneratedReportsMetaQueryVariables,
  InboxItemDocument,
  InboxItemQueryVariables,
  InboxItemsScrollableListDocument,
  InboxItemsScrollableListQueryVariables,
  useApproveVerificationRequestByVerifierMutation,
  VerificationRequestDocument,
  VerificationRequestPaymentInfoDocument,
  VerificationRequestPaymentInfoQueryVariables,
  VerificationRequestQueryVariables,
  VerificationRequestType,
} from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useVerificationRequestProcessing } from 'src/hooks/verificationRequest';

import { ApproveOrDeclineVerificationRequestModalContent } from '../ApproveOrDeclineVerificationRequestModalContent';

type ApproveVerificationRequestByVerifierModalContentProps = {
  onCloseModal: () => void;
  switchClosability: (closable: boolean) => void;
  inboxItemId: string;
  verificationRequestType: Nullable<VerificationRequestType>;
  permissiblePurposeId: string;
  paymentMethodId: Nullable<string>;
};

export const ApproveVerificationRequestByVerifierModalContent: React.FC<
  ApproveVerificationRequestByVerifierModalContentProps
> = ({
  onCloseModal,
  switchClosability,
  inboxItemId,
  verificationRequestType,
  permissiblePurposeId,
  paymentMethodId,
}) => {
  const { createApolloContext } = useNotification();

  const [needToRedirect, setNeedToRedirect] = React.useState(false);

  const { isVerifierResponding, startVerifierResponding, stopVerifierResponding } =
    useVerificationRequestProcessing();
  const isVerifierResponseInProgress = isVerifierResponding(inboxItemId);

  const refetchQueries = React.useMemo(() => {
    return [
      {
        query: InboxItemDocument,
        variables: { id: inboxItemId } satisfies InboxItemQueryVariables,
      },
      {
        query: InboxItemsScrollableListDocument,
        variables: {
          where: { id: { equals: inboxItemId } },
        } satisfies InboxItemsScrollableListQueryVariables,
      },
      {
        query: VerificationRequestDocument,
        variables: { filter: { inboxItemId } } satisfies VerificationRequestQueryVariables,
      },
      {
        query: VerificationRequestPaymentInfoDocument,
        variables: {
          where: { inboxItemId },
        } satisfies VerificationRequestPaymentInfoQueryVariables,
      },
      {
        query: GeneratedReportsMetaDocument,
        variables: { inboxItemId } satisfies GeneratedReportsMetaQueryVariables,
      },
    ];
  }, [inboxItemId]);

  const [approveVerificationRequest] = useApproveVerificationRequestByVerifierMutation({
    refetchQueries,
    context: createApolloContext({
      errorMessage: 'Failed to approve verification request',
    }),
  });

  const handleAction = React.useCallback(async () => {
    switchClosability(false);
    startVerifierResponding(inboxItemId);

    try {
      const { data } = await approveVerificationRequest({
        variables: { inboxItemId, verificationRequestType, permissiblePurposeId, paymentMethodId },
      });

      const redirectUrl = data?.approveVerificationRequestByVerifier.redirectUrl;
      if (redirectUrl) {
        setNeedToRedirect(true);
        window.location.replace(redirectUrl);
        return;
      }
      // biome-ignore lint/correctness/noUnusedVariables: do nothing
    } catch (error) {
      //
    }

    stopVerifierResponding(inboxItemId);
    switchClosability(true);
    onCloseModal();
  }, [
    approveVerificationRequest,
    inboxItemId,
    onCloseModal,
    paymentMethodId,
    permissiblePurposeId,
    startVerifierResponding,
    stopVerifierResponding,
    switchClosability,
    verificationRequestType,
  ]);

  return (
    <ApproveOrDeclineVerificationRequestModalContent
      action="approve"
      onCloseModal={onCloseModal}
      handleAction={handleAction}
      disabled={isVerifierResponseInProgress || needToRedirect}
      loading={isVerifierResponseInProgress || needToRedirect}
    />
  );
};
